import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import getScientificPublicationValidationSchema from "views/home/scientificPublication/form/ScientificPublicationValidationSchema";
import { CreateScientificPublicationRequest, ScientificPublicationStatus } from "views/home/scientificPublication/form/ScientificPublicationForm.types";
import SPFormAttachmentsTable from "views/home/scientificPublication/form/components/SPFormAttachmentsTable";
import SPFormMainInfo from "views/home/scientificPublication/form/components/SPFormMainInfo";
import SPFormDetails from "views/home/scientificPublication/form/components/SPFormDetails";
import { CourseOrderLocalItemResult } from "services/courseOrder/models/CourseOrderType";
import { useEffect, forwardRef, useImperativeHandle, useState } from "react";
import Switch from "components/blocks/Switch";
import { useQuery } from "hooks/useQuery";
import { ScientificPublicationsService } from "services/scientificPublicationsService";
import LoadingOverlay from "components/blocks/LoadingOverlay";

const now = new Date();

type ScientificPublicationFormProps = {
  order: CourseOrderLocalItemResult | null;
  submitRef?: any;
  scientificPublicationId?: number;
};

export type ScientificPublicationFormRef = {
  submit: () => void;
  getFormData: () => CreateScientificPublicationRequest;
  isValid: () => Promise<boolean>;
};

export const ScientificPublicationForm = forwardRef<ScientificPublicationFormRef, ScientificPublicationFormProps>(
  ({ order, submitRef, scientificPublicationId }, ref) => {
    const { t } = useTranslation("ScientificPublication");
    const scientificPublicationValidationSchema = getScientificPublicationValidationSchema(t);
    const [existingAttachments, setExistingAttachments] = useState<any[]>([]);

    // Fetch existing scientific publication if ID is provided
    const { data: existingPublication, loading: isLoadingPublication } = useQuery({
      queryFn: () => ScientificPublicationsService.getScientificPublicationById(scientificPublicationId!),
      options: {
        enabled: !!scientificPublicationId,
      },
    });

    const {
      formState: { errors },
      handleSubmit,
      register,
      watch,
      getValues,
      setValue,
      reset,
      trigger,
    } = useForm<CreateScientificPublicationRequest>({
      resolver: zodResolver(scientificPublicationValidationSchema),
      defaultValues: {
        status: ScientificPublicationStatus.NotPublishing,
        scientificPublishingDetails: [
          {
            locale: "Ar",
            displayName: "",
            description: "",
          },
          {
            locale: "En",
            displayName: "",
            description: "",
          },
        ],
        scientificPublishingAttachments: [],
        searchWords: [],
        librariesIds: [],
        scientificSpecializationId: 0,
        typeOfScientificId: 0,
        scientificClassificationId: 0,
        languageOfScientificId: 0,
        publisherId: 0,
        publisherTypeId: 0,
        numberOfPaper: 0,
        scientificPublicationLink: "",
        scientificPublishingImageId: "",
        metaData: "",
        isHardCopyAvailable: false,
        authorName: "",
        publicationDate: now,
        serialNumber: "",
      },
    });

    // Map existing publication data to form when loaded
    useEffect(() => {
      if (existingPublication) {
        const pub = existingPublication;
        reset({
          serialNumber: pub.serialNumber || "",
          authorName: pub.authorName || "",
          publicationDate: pub.publicationDate ? new Date(pub.publicationDate) : now,
          scientificSpecializationId: pub.scientificSpecialization?.id || 0,
          typeOfScientificId: pub.typeOfScientific?.id || 0,
          scientificClassificationId: pub.scientificClassification?.id || 0,
          languageOfScientificId: pub.languageOfScientific?.id || 0,
          publisherId: pub.publisher?.id || 0,
          publisherTypeId: pub.publisherType?.id || 0,
          numberOfPaper: pub.numberOfPaper || 0,
          scientificPublicationLink: pub.scientificPublicationLink || "",
          scientificPublishingImageId: pub.scientificPublishingImage?.id || "",
          metaData: pub.metaData || "",
          isHardCopyAvailable: pub.isHardCopyAvailable || false,
          status: pub.status === "Publishing" ? ScientificPublicationStatus.Publishing : ScientificPublicationStatus.NotPublishing,
          scientificPublishingDetails: pub.scientificPublishingDetails || [
            {
              locale: "Ar",
              displayName: "",
              description: "",
            },
            {
              locale: "En",
              displayName: "",
              description: "",
            },
          ],
          searchWords: pub.searchWords?.map((sw) => ({ searchWord: sw.searchWord })) || [],
          librariesIds: pub.libraries?.map((lib) => lib.id) || [],
          scientificPublishingAttachments: [],
        });

        // Set existing attachments for the table
        if (pub.scientificPublishingAttachments) {
          setExistingAttachments(pub.scientificPublishingAttachments);
        }
      }
    }, [existingPublication, reset]);

    // Map order data to form when order is loaded (fallback if no existing publication)
    useEffect(() => {
      if (order && !existingPublication) {
        reset({
          serialNumber: order.isbn || "",
          authorName: order.appliedUser?.arabicName || order.appliedUser?.englishName || "",
          publicationDate: now,
          scientificSpecializationId: order.scientificSpecialization?.id || 0,
          typeOfScientificId: order.typeOfScientific?.id || 0,
          scientificClassificationId: order.scientificClassification?.id || 0,
          languageOfScientificId: order.languageOfScientific?.id || 0,
          publisherId: 0,
          publisherTypeId: 0,
          numberOfPaper: order.numberOfPages || 0,
          scientificPublicationLink: "",
          scientificPublishingImageId: "",
          metaData: "",
          isHardCopyAvailable: false,
          status: ScientificPublicationStatus.NotPublishing,
          scientificPublishingDetails: [
            {
              locale: "Ar",
              displayName: "",
              description: "",
            },
            {
              locale: "En",
              displayName: "",
              description: "",
            },
          ],
          searchWords: [],
          librariesIds: [],
          scientificPublishingAttachments: [],
        });
      }
    }, [order, existingPublication, reset]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit((data) => {
          // This will be handled by the parent
        })();
      },
      getFormData: () => getValues(),
      isValid: async () => {
        return await trigger();
      },
    }));

    // Also expose to submitRef if provided
    useEffect(() => {
      if (submitRef) {
        submitRef.current = {
          submit: () => {
            handleSubmit((data) => {
              // This will be handled by the parent
            })();
          },
          getFormData: () => getValues(),
          isValid: async () => {
            return await trigger();
          },
        };
      }
    }, [submitRef, handleSubmit, getValues, trigger]);

    return (
      <div className="position-relative">
        <LoadingOverlay visible={isLoadingPublication} />
        <div className="d-flex flex-column gap-3 px-4">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="fw-bold text-dark">{t("scientificPublicationForm")}</h5>
            <div className="d-flex gap-2 align-items-center">
              <span className="text-dark">{t("publishingStatus")}</span>
              <Switch
                checked={watch("status") === ScientificPublicationStatus.Publishing}
                onChange={(checked) => {
                  setValue(
                    "status",
                    checked ? ScientificPublicationStatus.Publishing : ScientificPublicationStatus.NotPublishing,
                  );
                }}
              />
            </div>
          </div>
          <div className="divider" />
          <SPFormMainInfo register={register} setValue={setValue} watch={watch} errors={errors} />
          <div className="divider" />
          <SPFormDetails register={register} setValue={setValue} watch={watch} errors={errors} getValues={getValues} />
          <div className="divider" />
          <SPFormAttachmentsTable
            setValue={setValue}
            attachments={existingAttachments}
            viewOnly={false}
          />
        </div>
      </div>
    );
  }
);

ScientificPublicationForm.displayName = "ScientificPublicationForm";